import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError(t("auth.resetMissingToken")); return; }
    if (password !== confirm) { setError(t("auth.passwordsNoMatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("auth.resetSuccess"));
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Seo title="Reset Your Password" description="Set a new password for your Fire Alarm Blueprint Trainer account." path="/reset-password" noindex />
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A192F] blueprint-grid relative">
        <Link to="/" className="relative z-10">
          <img src="/logo-firealarmbt-light.png" alt="FireAlarm BT — Fire Alarm Blueprint Trainer" className="h-9 w-auto" />
        </Link>
        <div className="relative z-10 max-w-md">
          <h2 className="font-heading text-3xl font-black leading-tight mb-4">{t("auth.resetTitle")}</h2>
          <p className="text-slate-400">{t("auth.resetSubtitle")}</p>
        </div>
        <p className="text-xs text-slate-600 font-mono relative z-10">{t("footer.disclaimerTitle")}</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="reset-password-form">
          <h1 className="font-heading text-3xl font-black mb-1">{t("auth.resetTitle")}</h1>
          <p className="text-slate-400 mb-8 text-sm">{t("auth.resetSubtitle")}</p>

          {error && <div data-testid="reset-password-error" className="mb-4 text-sm rounded-md border border-primary/40 bg-primary/10 text-red-200 px-3 py-2">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label htmlFor="new-password" className="text-slate-300">{t("auth.newPassword")}</Label>
              <PasswordInput id="new-password" data-testid="reset-password-new" required minLength={8} value={password}
                onChange={(e) => setPassword(e.target.value)}
                containerClassName="mt-1.5" className="bg-secondary border-white/15 focus-visible:ring-primary" placeholder="••••••••" />
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-slate-300">{t("auth.confirmPassword")}</Label>
              <PasswordInput id="confirm-password" data-testid="reset-password-confirm" required minLength={8} value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                containerClassName="mt-1.5" className="bg-secondary border-white/15 focus-visible:ring-primary" placeholder="••••••••" />
            </div>
          </div>

          <Button type="submit" data-testid="reset-password-submit" disabled={loading}
            className="w-full mt-6 bg-primary hover:bg-primary/90 text-white h-11">
            {loading ? <Loader2 className="animate-spin" size={18} /> : t("auth.resetCta")}
          </Button>

          <p className="text-sm text-slate-400 mt-6 text-center">
            <Link to="/login" className="text-primary hover:underline font-medium">{t("auth.signInLink")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
